import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { analytics } from '@/lib/analytics';
import type { Organization } from '@/types/core';
import { useTimeFilterStore } from './timeFilterStore';

interface OrgState {
  currentOrg: Organization | null;
  organizations: Organization[];
  setCurrentOrg: (org: Organization) => void;
  setOrganizations: (orgs: Organization[]) => void;
  /**
   * Clear currentOrg. When `autoSelectAlternate` is true, pick the next
   * available org (excluding the one we're clearing). Used by the 403
   * interceptor when the user loses access to their current org so they
   * always land on a valid one without a dead UI.
   */
  clearCurrentOrg: (opts?: { autoSelectAlternate?: boolean }) => void;
}

export const useOrgStore = create<OrgState>()(
  persist(
    (set, get) => ({
      currentOrg: null,
      organizations: [],

      setCurrentOrg: (org: Organization) => {
        // Clear repo and team filters when switching orgs (they are org-specific)
        useTimeFilterStore.getState().setSelectedRepoId(null);
        useTimeFilterStore.getState().setSelectedTeamId(null);

        set({ currentOrg: org });

        // Update analytics with new org context
        analytics.updateUser({
          orgId: org.id,
          orgSlug: org.slug,
          orgName: org.name,
          isPersonal: org.is_personal,
        });
      },

      setOrganizations: (orgs: Organization[]) => {
        set((state) => {
          // Reconcile currentOrg with the new orgs list:
          // - if currentOrg still exists, adopt its FRESH copy from the list so
          //   org-level settings (e.g. individual_metrics) are never served from a
          //   stale persisted snapshot in localStorage
          // - if currentOrg is missing or was removed, fall back to the first org
          const nextCurrent =
            orgs.find((o) => o.id === state.currentOrg?.id) ?? orgs[0] ?? null;
          return { organizations: orgs, currentOrg: nextCurrent };
        });
      },

      clearCurrentOrg: (opts) => {
        useTimeFilterStore.getState().setSelectedRepoId(null);
        useTimeFilterStore.getState().setSelectedTeamId(null);
        const { currentOrg, organizations } = get();
        const failedId = currentOrg?.id;

        if (opts?.autoSelectAlternate) {
          const alternate = organizations.find((o) => o.id !== failedId);
          if (alternate) {
            set({ currentOrg: alternate });
            analytics.updateUser({
              orgId: alternate.id,
              orgSlug: alternate.slug,
              orgName: alternate.name,
              isPersonal: alternate.is_personal,
            });
            return;
          }
        }
        set({ currentOrg: null });
      },
    }),
    {
      name: 'org-storage',
    }
  )
);
